import {
  hasOneTrustSdkGlobal,
  setOneTrustScriptStatus,
} from "./OneTrustScriptStatus";

const ONE_TRUST_SDK_URL =
  "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
const INIT_SCRIPT_ID = "onetrust-cookies-init";
const SDK_SCRIPT_ID = "onetrust-cookies-script";
const ONE_TRUST_READY_TIMEOUT_MS = 10_000;

/**
 * Injects the OneTrust SDK into the page. Call once from a useEffect in the
 * host app (e.g. _app.tsx). Safe to call multiple times — guards with IDs.
 *
 * Kept as a plain DOM script-injection helper rather than a React
 * component so host apps control when it runs and can pass framework-specific
 * values such as CSP nonces.
 */
export function initOneTrustScripts(
  domainScript: string | undefined,
  nonce?: string,
): void {
  if (!domainScript || typeof document === "undefined") {
    setOneTrustScriptStatus("disabled");
    return;
  }

  setOneTrustScriptStatus("loading");

  const scheduleReadinessCheck = () =>
    setTimeout(() => {
      setOneTrustScriptStatus(hasOneTrustSdkGlobal() ? "ready" : "failed");
    }, ONE_TRUST_READY_TIMEOUT_MS);

  if (!document.getElementById(INIT_SCRIPT_ID)) {
    const initScript = document.createElement("script");
    initScript.id = INIT_SCRIPT_ID;
    initScript.type = "text/javascript";
    if (nonce) initScript.setAttribute("nonce", nonce);
    // OneTrust calls OptanonWrapper once the SDK is ready and again on every
    // consent change. Re-broadcasting it lets consumers re-read
    // window.OnetrustActiveGroups, which does not exist yet when they mount.
    initScript.text =
      'function OptanonWrapper() { window.dispatchEvent(new Event("OneTrustGroupsUpdated")); }';
    document.head.appendChild(initScript);
  }

  const existingSdkScript = document.getElementById(
    SDK_SCRIPT_ID,
  ) as HTMLScriptElement | null;
  if (existingSdkScript) {
    if (hasOneTrustSdkGlobal()) {
      setOneTrustScriptStatus("ready");
      return;
    }

    const readinessTimeout = scheduleReadinessCheck();
    existingSdkScript.addEventListener(
      "load",
      () => {
        if (!hasOneTrustSdkGlobal()) return;

        clearTimeout(readinessTimeout);
        setOneTrustScriptStatus("ready");
      },
      { once: true },
    );
    existingSdkScript.addEventListener(
      "error",
      () => {
        clearTimeout(readinessTimeout);
        setOneTrustScriptStatus("failed");
      },
      { once: true },
    );
    return;
  }

  const sdkScript = document.createElement("script");
  let readinessTimeout: ReturnType<typeof setTimeout> | undefined;
  sdkScript.id = SDK_SCRIPT_ID;
  sdkScript.type = "text/javascript";
  sdkScript.src = ONE_TRUST_SDK_URL;
  sdkScript.async = true;
  sdkScript.setAttribute("charset", "UTF-8");
  sdkScript.dataset.domainScript = domainScript;
  if (nonce) sdkScript.setAttribute("nonce", nonce);
  sdkScript.onload = () => {
    if (!hasOneTrustSdkGlobal()) return;

    if (readinessTimeout) clearTimeout(readinessTimeout);
    setOneTrustScriptStatus("ready");
  };
  sdkScript.onerror = () => {
    if (readinessTimeout) clearTimeout(readinessTimeout);
    setOneTrustScriptStatus("failed");
  };
  readinessTimeout = scheduleReadinessCheck();
  document.head.appendChild(sdkScript);
}
